import type { DeviceType } from '@talos/kyoko';
import {
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventSource,
  Tooltip,
  useDisclosure,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useAppStateSelector } from '../../../providers/AppStateProvider';
import type { LayoutSection, LayoutSpec } from '../../../types/LayoutConfig';
import { selectCurrentLayout } from '../../AppLayout/AppLayoutSlice';
import type { LayoutSpecResponse } from '../../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { useSectionSpaceMetadata } from '../hooks/useSectionSpaceMetadata';
import { ManageSpaceModal } from './ManageSpaceModal/ManageSpaceModal';
import { ManageTemplateModal } from './ManageTemplateModal/ManageTemplateModal';

interface ManageSpaceButtonsProps {
  onAddNewSpace: (newSpaceName: string, selectedTemplate: LayoutSpec, deviceType: DeviceType, group?: string) => void;
  layoutSection: LayoutSection;
  isRearranging: boolean;
  onSaveRearrange: () => void;
  onCancelRearrange: () => void;
}

export function ManageSpaceButtons({
  onAddNewSpace,
  layoutSection,
  isRearranging,
  onSaveRearrange,
  onCancelRearrange,
}: ManageSpaceButtonsProps) {
  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState<LayoutSpecResponse>();
  const mixpanel = useMixpanel();
  const layoutSpec = useAppStateSelector(selectCurrentLayout);
  const addSpaceDisclosure = useDisclosure();
  const manageTemplateDisclosure = useDisclosure({
    onClose() {
      setSelectedTemplateConfig(undefined);
    },
  });

  const { existingGroups, sectionLayoutIdsAndLabels } = useSectionSpaceMetadata(layoutSection);

  const handleAddSpace = useCallback(() => {
    addSpaceDisclosure.open();
    mixpanel.track(MixpanelEvent.OpenAddNewSpaceModal);
  }, [addSpaceDisclosure, mixpanel]);

  const handleOpenCreateTemplate = useCallback(() => {
    manageTemplateDisclosure.open();
    mixpanel.track(MixpanelEvent.OpenCreateTemplateModal, { section: layoutSection });
  }, [manageTemplateDisclosure, mixpanel, layoutSection]);

  const handleEditTemplate = useCallback(
    (config: LayoutSpecResponse) => {
      mixpanel.track(MixpanelEvent.OpenEditTemplateModal, {
        section: layoutSection,
        source: MixpanelEventSource.NavigationSidebar,
      });
      addSpaceDisclosure.close();
      setSelectedTemplateConfig(config);
      manageTemplateDisclosure.open();
    },
    [manageTemplateDisclosure, addSpaceDisclosure, mixpanel, layoutSection]
  );

  if (isRearranging) {
    return (
      <Flex gap="spacing8">
        <Button
          data-testid="rearrange-save-button"
          size={FormControlSizes.Small}
          variant={ButtonVariants.Positive}
          onClick={onSaveRearrange}
          width="100%"
        >
          Save
        </Button>
        <Tooltip tooltip="Cancel" closeOnClickOutside={true}>
          <Button data-testid="rearrange-cancel-button" size={FormControlSizes.Small} onClick={onCancelRearrange}>
            Cancel
          </Button>
        </Tooltip>
      </Flex>
    );
  }

  return (
    <>
      <Flex gap="spacing8">
        <Button
          size={FormControlSizes.Small}
          onClick={handleAddSpace}
          dim
          startIcon={IconName.Plus}
          data-testid="new-space-button"
          width="100%"
        >
          New Space
        </Button>
        <Tooltip tooltip="Create a new template" closeOnClickOutside={true}>
          <IconButton
            data-testid="create-template-button"
            dim
            icon={IconName.Template}
            size={FormControlSizes.Small}
            onClick={handleOpenCreateTemplate}
          />
        </Tooltip>
      </Flex>
      {addSpaceDisclosure.isOpen && (
        <ManageSpaceModal
          onAddNewSpace={onAddNewSpace}
          layoutSection={layoutSection}
          onEditTemplate={handleEditTemplate}
          existingGroups={existingGroups}
          sectionLayoutIdsAndLabels={sectionLayoutIdsAndLabels}
          {...addSpaceDisclosure}
        />
      )}
      {manageTemplateDisclosure.isOpen && (layoutSpec?.id || selectedTemplateConfig) && (
        <ManageTemplateModal
          layoutSection={layoutSection}
          initialLayoutId={layoutSpec?.id}
          selectedTemplateConfig={selectedTemplateConfig}
          {...manageTemplateDisclosure}
        />
      )}
    </>
  );
}
